import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  Input,
  Form,
  Space,
  Empty,
  Card,
  Tag,
} from "antd";
import {
  FileExcelFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import withClinic from "../../hooks/with_clinic";
import DateRange from "../../components/date_range";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import {
  ApolloResult,
  Clinic,
  MainPaymentViewResult,
  MainPaymentViewResponse,
} from "../../store";
import {
  get_payment_report,
  getPaymentReportVar,
} from "../../graphql/order";
import LoadingSpinner from "../../components/loading_indicator";
import Error from "../../components/apollo_error";
import { formatMoney, getCurrency } from "../../helpers/utils";
import { PaginationConfig } from "antd/lib/pagination";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

interface P {
  currentClinic?: Clinic;
}

interface PaymentRecord extends MainPaymentViewResult {
  key?: string;
}

interface SummaryRecord {
  PaymentMethod: string;
  TotalAmount: number;
  TransactionCount: number;
}

const hasRowLevelPaymentMethod = (record: MainPaymentViewResult) =>
  !!record.PaymentMethod?.trim();

const hasRowLevelPaymentAmount = (record: MainPaymentViewResult) =>
  record.PaymentAmount != null && record.PaymentAmount !== 0;

const buildUnpaidInvoiceNumbers = (rows: MainPaymentViewResult[]) => {
  const unpaidSet = new Set<string>();
  rows.forEach((record) => {
    if (
      record.PaymentStatus?.trim().toUpperCase() === "UNPAID" &&
      record.InvoiceNumber
    ) {
      unpaidSet.add(record.InvoiceNumber);
    }
  });
  return unpaidSet;
};

const isUnpaidInvoice = (
  record: MainPaymentViewResult,
  unpaidInvoiceNumbers: Set<string>
) => !!record.InvoiceNumber && unpaidInvoiceNumbers.has(record.InvoiceNumber);

const buildInvoicesWithRowPaymentMethod = (
  rows: MainPaymentViewResult[],
  unpaidInvoiceNumbers: Set<string>
) => {
  const invoiceSet = new Set<string>();
  rows.forEach((record) => {
    if (
      !isUnpaidInvoice(record, unpaidInvoiceNumbers) &&
      hasRowLevelPaymentMethod(record) &&
      record.InvoiceNumber
    ) {
      invoiceSet.add(record.InvoiceNumber);
    }
  });
  return invoiceSet;
};

const buildInvoicesWithRowPaymentAmount = (
  rows: MainPaymentViewResult[],
  unpaidInvoiceNumbers: Set<string>
) => {
  const invoiceSet = new Set<string>();
  rows.forEach((record) => {
    if (
      !isUnpaidInvoice(record, unpaidInvoiceNumbers) &&
      hasRowLevelPaymentAmount(record) &&
      record.InvoiceNumber
    ) {
      invoiceSet.add(record.InvoiceNumber);
    }
  });
  return invoiceSet;
};

const getDisplayPaymentMethod = (
  record: MainPaymentViewResult,
  invoicesWithRowPaymentMethod: Set<string>,
  unpaidInvoiceNumbers: Set<string>
) => {
  if (isUnpaidInvoice(record, unpaidInvoiceNumbers)) {
    return "";
  }

  if (hasRowLevelPaymentMethod(record)) {
    return record.PaymentMethod.trim();
  }

  const invoiceNumber = record.InvoiceNumber || "";
  if (invoiceNumber && invoicesWithRowPaymentMethod.has(invoiceNumber)) {
    return "";
  }

  return record.OrderPaymentMethod?.trim() || "";
};

const getDisplayPaymentAmount = (
  record: MainPaymentViewResult,
  invoicesWithRowPaymentAmount: Set<string>,
  unpaidInvoiceNumbers: Set<string>
): number | null => {
  if (isUnpaidInvoice(record, unpaidInvoiceNumbers)) {
    return null;
  }

  if (hasRowLevelPaymentAmount(record)) {
    return record.PaymentAmount;
  }

  const invoiceNumber = record.InvoiceNumber || "";
  if (invoiceNumber && invoicesWithRowPaymentAmount.has(invoiceNumber)) {
    return null;
  }

  const orderNetTotal = record.OrderNetTotal;
  return orderNetTotal == null || orderNetTotal === 0 ? null : orderNetTotal;
};

const PaymentReportPage = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";
  const client = useApolloClient();
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState<PaginationConfig | null>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
  });
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState(() => {
    const today = new Date();

    const start = new Date(today);
    start.setHours(0, 0, 0, 0);

    const end = new Date(today);
    end.setHours(23, 59, 59, 999);

    return { startDate: start, endDate: end };
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);

  const variables = getPaymentReportVar({
    clinicCode,
    filterType: "day",
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    skip: 0,
    take: 100000,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"getPaymentReport", MainPaymentViewResponse>
  >(get_payment_report, {
    variables,
    skip: !clinicCode,
    fetchPolicy: "cache-and-network",
  });

  console.log('variables',variables);
  console.log('error',error);

  const unpaidInvoiceNumbers = useMemo(
    () => buildUnpaidInvoiceNumbers(data?.getPaymentReport?.data || []),
    [data?.getPaymentReport?.data]
  );

  const invoicesWithRowPaymentMethod = useMemo(
    () =>
      buildInvoicesWithRowPaymentMethod(
        data?.getPaymentReport?.data || [],
        unpaidInvoiceNumbers
      ),
    [data?.getPaymentReport?.data, unpaidInvoiceNumbers]
  );

  const invoicesWithRowPaymentAmount = useMemo(
    () =>
      buildInvoicesWithRowPaymentAmount(
        data?.getPaymentReport?.data || [],
        unpaidInvoiceNumbers
      ),
    [data?.getPaymentReport?.data, unpaidInvoiceNumbers]
  );

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!data?.getPaymentReport?.data) return [];
    
    let filtered = data.getPaymentReport.data;

    // Apply search filter across multiple fields
    if (searchTerm.trim() !== "") {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (record) =>
          (record.InvoiceNumber?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.MemberId?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.SalePerson?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServiceName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServicePackageName?.toLowerCase().includes(
            normalizedSearchTerm
          ) || false)
      );
    }

    return filtered;
  }, [
    data?.getPaymentReport?.data,
    searchTerm,
  ]);

  // Generate summary data from filtered data
  const summaryData = useMemo(() => {
    const summary: Record<string, SummaryRecord> = {};
    const countedOrderNetTotalInvoices = new Set<string>();

    filteredData.forEach((record) => {
      const paymentMethod = getDisplayPaymentMethod(
        record,
        invoicesWithRowPaymentMethod,
        unpaidInvoiceNumbers
      );
      const paymentAmount = getDisplayPaymentAmount(
        record,
        invoicesWithRowPaymentAmount,
        unpaidInvoiceNumbers
      );
      if (!paymentMethod || paymentAmount == null) {
        return;
      }

      if (
        !hasRowLevelPaymentAmount(record) &&
        record.InvoiceNumber &&
        countedOrderNetTotalInvoices.has(record.InvoiceNumber)
      ) {
        return;
      }

      if (!hasRowLevelPaymentAmount(record) && record.InvoiceNumber) {
        countedOrderNetTotalInvoices.add(record.InvoiceNumber);
      }

      if (!summary[paymentMethod]) {
        summary[paymentMethod] = {
          PaymentMethod: paymentMethod,
          TotalAmount: 0,
          TransactionCount: 0,
        };
      }
      summary[paymentMethod].TotalAmount += paymentAmount;
      summary[paymentMethod].TransactionCount += 1;
    });

    return Object.values(summary).sort(
      (a, b) => b.TotalAmount - a.TotalAmount
    );
  }, [filteredData, invoicesWithRowPaymentMethod, invoicesWithRowPaymentAmount, unpaidInvoiceNumbers]);

  const detailData = useMemo(() => {
    if (!selectedPaymentMethod) return filteredData;
    return filteredData.filter(
      (record) =>
        getDisplayPaymentMethod(record, invoicesWithRowPaymentMethod, unpaidInvoiceNumbers) ===
        selectedPaymentMethod
    );
  }, [filteredData, selectedPaymentMethod, invoicesWithRowPaymentMethod, unpaidInvoiceNumbers]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [searchTerm, selectedPaymentMethod, ranges.startDate, ranges.endDate]);

  const onDateChange = (startDate: Date, endDate: Date) => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    setRanges({ startDate: start, endDate: end });
  };

  const exportToExcel = async () => {
    if (!clinicCode) return;
    setExporting(true);

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    try {
      // Fetch all transactions for export (not paginated)
      const exportVariables = getPaymentReportVar({
        clinicCode,
        filterType: "day",
        startDate: ranges.startDate,
        endDate: ranges.endDate,
        skip: 0,
        take: 100000, // Large number to get all records
      });

      const exportResult = await client.query<
        ApolloResult<"getPaymentReport", MainPaymentViewResponse>
      >({
        query: get_payment_report,
        variables: exportVariables,
        fetchPolicy: "network-only",
      });

      // Get all transactions (not filtered)
      const allTransactions = exportResult.data?.getPaymentReport?.data || [];

      if (allTransactions.length === 0) {
        setExporting(false);
        return;
      }

      const matchesSearchTerm = (record: PaymentRecord) => {
        if (searchTerm.trim() === "") return true;
        const normalizedSearchTerm = searchTerm.toLowerCase().trim();
        return (
          (record.InvoiceNumber?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.MemberId?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.SalePerson?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServiceName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServicePackageName?.toLowerCase().includes(
            normalizedSearchTerm
          ) || false)
        );
      };

      const exportUnpaidInvoiceNumbers =
        buildUnpaidInvoiceNumbers(allTransactions);
      const exportInvoicesWithRowPaymentMethod =
        buildInvoicesWithRowPaymentMethod(
          allTransactions,
          exportUnpaidInvoiceNumbers
        );
      const exportInvoicesWithRowPaymentAmount =
        buildInvoicesWithRowPaymentAmount(
          allTransactions,
          exportUnpaidInvoiceNumbers
        );

      const exportTransactions = allTransactions.filter((record) => {
        const paymentMethod = getDisplayPaymentMethod(
          record,
          exportInvoicesWithRowPaymentMethod,
          exportUnpaidInvoiceNumbers
        );
        const matchesPaymentMethod =
          !selectedPaymentMethod || paymentMethod === selectedPaymentMethod;
        return matchesPaymentMethod && matchesSearchTerm(record);
      });

      // Calculate summary from all transactions
      const allSummary: Record<string, SummaryRecord> = {};
      const countedOrderNetTotalInvoices = new Set<string>();
      exportTransactions.forEach((record: PaymentRecord) => {
        const paymentMethod = getDisplayPaymentMethod(
          record,
          exportInvoicesWithRowPaymentMethod,
          exportUnpaidInvoiceNumbers
        );
        const paymentAmount = getDisplayPaymentAmount(
          record,
          exportInvoicesWithRowPaymentAmount,
          exportUnpaidInvoiceNumbers
        );
        if (!paymentMethod || paymentAmount == null) {
          return;
        }

        if (
          !hasRowLevelPaymentAmount(record) &&
          record.InvoiceNumber &&
          countedOrderNetTotalInvoices.has(record.InvoiceNumber)
        ) {
          return;
        }

        if (!hasRowLevelPaymentAmount(record) && record.InvoiceNumber) {
          countedOrderNetTotalInvoices.add(record.InvoiceNumber);
        }

        if (!allSummary[paymentMethod]) {
          allSummary[paymentMethod] = {
            PaymentMethod: paymentMethod,
            TotalAmount: 0,
            TransactionCount: 0,
          };
        }
        allSummary[paymentMethod].TotalAmount += paymentAmount;
        allSummary[paymentMethod].TransactionCount += 1;
      });

      const allSummaryData = Object.values(allSummary).sort(
        (a, b) => b.TotalAmount - a.TotalAmount
      );

      // Calculate grand total from all data
      const totalGrandTotal = allSummaryData.reduce(
        (total, method) => total + method.TotalAmount,
        0
      );

      // Prepare summary data from all transactions
      const summaryRows = [
        ...allSummaryData.map((row) => ({
          "Payment Method": row.PaymentMethod,
          "Transaction Count": row.TransactionCount,
          "Total Amount": row.TotalAmount === 0 ? "" : `${formatMoney(row.TotalAmount)}`,
        })),
        {
          "Payment Method": "Grand Total",
          "Transaction Count": allSummaryData.reduce(
            (count, method) => count + method.TransactionCount,
            0
          ),
          "Total Amount": `${formatMoney(totalGrandTotal)} ${getCurrency(
            props.currentClinic
          )}`,
        },
      ];

      // Prepare detailed transactions data from all transactions
      const transactionRows = exportTransactions.map((record: PaymentRecord) => {
        const walletValue = record.WalletTopUp || "";

        return {
          Date: record.Date,
          "Invoice Number": record.InvoiceNumber,
          "Customer Name": record.CustomerName,
          "Member ID": record.MemberId || "",
          "Sale Person": record.SalePerson,
          "Service Name": record.ServiceName || "",
          "Service Package": record.ServicePackageName || "",
          "Payment Method": getDisplayPaymentMethod(
            record,
            exportInvoicesWithRowPaymentMethod,
            exportUnpaidInvoiceNumbers
          ),
          "Payment Status": record.PaymentStatus|| "",
          "Payment Amount": (() => {
            const amount = getDisplayPaymentAmount(
              record,
              exportInvoicesWithRowPaymentAmount,
              exportUnpaidInvoiceNumbers
            );
            return amount == null ? "" : amount;
          })(),
          Wallet: walletValue,
          "Invoice Total": record.InvoiceNetTotal === 0 ? "" : record.InvoiceNetTotal,
        };
      });

      // Create worksheets
      const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
      const transactionWs = XLSX.utils.json_to_sheet(transactionRows);

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, summaryWs, "Payment Summary");
      XLSX.utils.book_append_sheet(wb, transactionWs, "Detailed Transactions");

      // Generate filename with date range
      const dateStr =
        format(ranges.startDate, "yyyy-MMM-dd") +
        "_" +
        format(ranges.endDate, "yyyy-MMM-dd");
      const safeMethod = selectedPaymentMethod
        ? selectedPaymentMethod.replace(/[^\w-]+/g, "_")
        : "";
      const fileName = `payment_report_${dateStr}${
        safeMethod ? `_${safeMethod}` : ""
      }`;

      // Export file
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(blob, fileName + fileExtension);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data?.getPaymentReport) return <Empty />;

  const grandTotal = summaryData.reduce(
    (total, method) => total + method.TotalAmount,
    0
  );

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      width: 120,
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        new Date(a.Date).getTime() - new Date(b.Date).getTime(),
      render: (v: string) => {
        try {
          return format(new Date(v), "dd MMM, yyyy");
        } catch {
          return v;
        }
      },
    },
    {
      title: "Invoice Number",
      dataIndex: "InvoiceNumber",
      key: "InvoiceNumber",
      width: 150,
      ellipsis: false,
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        a.InvoiceNumber?.localeCompare(b.InvoiceNumber || "") || 0,
    },
    {
      title: "Customer Name",
      dataIndex: "CustomerName",
      key: "CustomerName",
      width: 150,
      ellipsis: false,
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        a.CustomerName?.localeCompare(b.CustomerName || "") || 0,
    },
    {
      title: "Member ID",
      dataIndex: "MemberId",
      key: "MemberId",
      width: 120,
      ellipsis: false,
    },
    {
      title: "Sale Person",
      dataIndex: "SalePerson",
      key: "SalePerson",
      width: 130,
      ellipsis: false,
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        a.SalePerson?.localeCompare(b.SalePerson || "") || 0,
    },
    {
      title: "Service Name",
      dataIndex: "ServiceName",
      key: "ServiceName",
      width: 200,
      ellipsis: false,
    },
    {
      title: "Service Package",
      dataIndex: "ServicePackageName",
      key: "ServicePackageName",
      width: 150,
      ellipsis: false,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Payment Method",
      dataIndex: "PaymentMethod",
      key: "PaymentMethod",
      width: 150,
      ellipsis: false,
      render: (_: string, record: PaymentRecord) =>
        getDisplayPaymentMethod(record, invoicesWithRowPaymentMethod, unpaidInvoiceNumbers) || "-",
    },
    {
      title: "Payment Status",
      dataIndex: "PaymentStatus",
      key: "PaymentStatus",
      width: 150,
      ellipsis: false,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Payment Amount",
      dataIndex: "PaymentAmount",
      key: "PaymentAmount",
      width: 130,
      align: "right",
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        (getDisplayPaymentAmount(a, invoicesWithRowPaymentAmount, unpaidInvoiceNumbers) || 0) -
        (getDisplayPaymentAmount(b, invoicesWithRowPaymentAmount, unpaidInvoiceNumbers) || 0),
      render: (_: number, record: PaymentRecord) => {
        const amount = getDisplayPaymentAmount(
          record,
          invoicesWithRowPaymentAmount,
          unpaidInvoiceNumbers
        );
        return amount == null
          ? "-"
          : `${formatMoney(amount)} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Wallet",
      dataIndex: "WalletTopUp",
      key: "WalletTopUp",
      width: 120,
      ellipsis: false,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Invoice Total",
      dataIndex: "InvoiceNetTotal",
      key: "InvoiceNetTotal",
      width: 130,
      align: "right",
      sorter: (a: PaymentRecord, b: PaymentRecord) =>
        a.InvoiceNetTotal - b.InvoiceNetTotal,
      render: (v: number) =>
        v === 0 ? "-" : `${formatMoney(v)} ${getCurrency(props.currentClinic)}`,
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Payment Report
            </Typography.Title>
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <Space>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <DateRange
            startDate={ranges.startDate}
            endDate={ranges.endDate}
            onSelect={onDateChange}
          />
        </Space>
      </Col>
    </Row>
  );

  const filters = (
    <Row style={{ marginTop: 4, marginRight: 2 }}>
      <Col span={2} offset={22}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={exportToExcel}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  const summaryTable = (
    <Card
      title="Payment Methods Summary"
      style={{ marginBottom: 16 }}
      extra={
        selectedPaymentMethod ? (
          <Space size={8}>
            <Typography.Text type="secondary">Filtered by:</Typography.Text>
            <Tag color="blue">{selectedPaymentMethod}</Tag>
            <Button size="small" onClick={() => setSelectedPaymentMethod(null)}>
              Clear
            </Button>
          </Space>
        ) : null
      }
    >
      {summaryData.length > 0 ? (
        <Table
          dataSource={summaryData}
          pagination={false}
          size="small"
          rowKey="PaymentMethod"
          onRow={(record: SummaryRecord) => ({
            onClick: () =>
              setSelectedPaymentMethod((current) =>
                current === record.PaymentMethod ? null : record.PaymentMethod
              ),
            style: { cursor: "pointer" },
          })}
          rowClassName={(record: SummaryRecord) =>
            record.PaymentMethod === selectedPaymentMethod
              ? "ant-table-row-selected"
              : ""
          }
          columns={[
            {
              title: "Payment Method",
              dataIndex: "PaymentMethod",
              key: "PaymentMethod",
              render: (value: string) =>
                value === selectedPaymentMethod ? <Tag color="blue">{value}</Tag> : value,
            },
            {
              title: "Transactions",
              dataIndex: "TransactionCount",
              key: "TransactionCount",
              align: "right",
              render: (v: number) => v.toLocaleString(),
            },
            {
              title: "Total Amount",
              dataIndex: "TotalAmount",
              key: "TotalAmount",
              align: "right",
              render: (v: number) =>
                `${formatMoney(v)} ${getCurrency(props.currentClinic)}`,
            },
          ]}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>Grand Total</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>
                    {summaryData
                      .reduce(
                        (count, method) => count + method.TransactionCount,
                        0
                      )
                      .toLocaleString()}
                  </Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Typography.Text strong>
                    {formatMoney(grandTotal)} {getCurrency(props.currentClinic)}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <Typography.Text type="secondary">
          No payment data available for the selected filters
        </Typography.Text>
      )}
    </Card>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Card
          title="Detailed Transactions"
          style={{ marginBottom: 16 }}
        >
          <Form form={form} component={false}>
            <HorizontalScrollContainer>
              <Table
                showHeader={detailData.length > 0}
                dataSource={detailData.map((b, index) => ({
                  key: `${b.InvoiceNumber}-${index}`,
                  ...b,
                }))}
                columns={columns}
                scroll={{ x: 1500 }}
                onChange={(p: PaginationConfig) => {
                  setPagination(p);
                }}
                pagination={{
                  total: detailData.length,
                  current: pagination?.current,
                  pageSize: pagination?.pageSize,
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30", "50", "100"],
                }}
              />
            </HorizontalScrollContainer>
          </Form>
        </Card>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {summaryTable}
      {context}
    </React.Fragment>
  );
};

export default withClinic(PaymentReportPage);

